import { Form } from 'antd';
import React, { FunctionComponent, useState } from 'react';
import { isEmail } from 'ts-utils';
import { QuestionWrapper } from '../../styles/Containers';
import { CopyContainer } from '../../styles/CopyContainer';
import { ErrorText } from '../../styles/ErrorMessage';
import { TextInput } from '../../styles/TextInput';
import { BodySM } from '../../styles/Typography';
import { LayoutActions } from './LayoutActions';

export type SingleInputLayoutConfig = (
  | {
      type: 'string';
    }
  | {
      type: 'number';
      minimumValue?: number;
    }
  | {
      /** Next stays disabled until the value is a syntactically valid email. */
      type: 'email';
    }
) & {
  /** If true, don't record answer in analytics */
  collectsPIIData: boolean;
  /** One paragraph, or several rendered as separate lines. */
  content: string | string[];
  inputLabel: string;
  inputPlaceholder?: string;
  selected?: string;
  characterLimit?: number;
  primaryButtonTitle: string;
  onPressPrimaryButton?: (value: string) => void;
};

/** HTML `type` attribute for each config type ('string' isn't a valid input type). */
const INPUT_TYPE_ATTRIBUTE: Record<SingleInputLayoutConfig['type'], string> = {
  string: 'text',
  number: 'number',
  email: 'email',
};

type SingleInputLayoutProps = SingleInputLayoutConfig & {
  onPressPrimaryButton: (value: string) => void;
};

export const SingleInputLayout: FunctionComponent<SingleInputLayoutProps> = (props) => {
  const { inputLabel, inputPlaceholder, selected: prefillValue } = props;
  const isNumberInput = props.type === 'number';
  const isEmailInput = props.type === 'email';
  const inputType = INPUT_TYPE_ATTRIBUTE[props.type];
  const contentLines = Array.isArray(props.content) ? props.content : [props.content];

  const [input, setInput] = useState(prefillValue ?? '');
  const [error, setError] = useState<null | string>(null);
  const disabled = input.length < 1 || (isEmailInput && !isEmail(input));

  function onPrimaryPress() {
    if (!disabled) {
      if (
        isNumberInput &&
        props.minimumValue !== undefined &&
        parseInt(input) < props.minimumValue
      ) {
        setError(`${inputPlaceholder} must be at least ${props.minimumValue}.`);
      } else {
        props.onPressPrimaryButton(input);
      }
    }
  }

  return (
    <Form>
      <QuestionWrapper>
        <div style={{ margin: 'auto' }}>
          <CopyContainer>
            {contentLines.map((line, index) => (
              <BodySM key={index} data-test={index === 0 ? 'surveyQuestion' : undefined}>
                {line}
              </BodySM>
            ))}
          </CopyContainer>
          <Form.Item>
            <TextInput
              type={inputType}
              autoComplete={isEmailInput ? 'email' : undefined}
              value={input}
              placeholder={inputPlaceholder}
              aria-label={inputLabel}
              data-testid="single-input-field"
              maxLength={props.characterLimit}
              onChange={({ target: { value } }) => setInput(value)}
            />
          </Form.Item>
          <ErrorText>{error}</ErrorText>
        </div>
        <LayoutActions
          primaryButtonTitle={props.primaryButtonTitle}
          onPressPrimaryButton={onPrimaryPress}
          primaryButtonDisabled={disabled}
        />
      </QuestionWrapper>
    </Form>
  );
};
